import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Badge,
  Button,
  Collapse,
  Dropdown,
  Empty,
  Input,
  List,
  Modal,
  Popover,
  Spin,
  Table,
  Tag,
  message,
} from "antd";
import {
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  MoreOutlined,
  PlusOutlined,
  SearchOutlined,
  SendOutlined,
} from "@ant-design/icons";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import Permissions from "pages/permissions";
import { AuthStorage } from "utils/auth-storage";
import { saveUserAccessLevel } from "redux/permission-reducer";
import { CompanyDirectoryService } from "apis/Services/CompanyDirectoryService";
import ProcoreLogo from "assets/images/Procore_Logo.svg";
import ProcoreLogoWhite from "assets/images/Procore_Logo_White.svg";
import ContactForm from "./contact-form";
import BulkEditForm from "./bulk-edit-form";
import {
  canManageContact,
  filterCompanies,
  getBulkEditDetails,
  getContactAssignmentDetails,
  getContactFormValues,
  getContactKey,
  getContactName,
  getContactProjects,
  getContactUpdateDetails,
  getErrorMessage,
  getInvitationDetails,
  getProjectOptions,
} from "./directory-utils";
import styles from "./directory.module.css";
import LoadingComponent from "components/loading-component";
import Text from "components/text";

const STATUS_COLORS = {
  Activated: "success",
  Pending: "processing",
  "Not Invited": "default",
};
const EMPTY_PERMISSIONS = {
  companyAssignablePermissions: [],
  projectAssignablePermissions: [],
};

const DirectoryContent = ({ companyId }) => {
  const dispatch = useDispatch();
  const [messageApi, messageContext] = message.useMessage();
  const [modalApi, modalContext] = Modal.useModal();
  const [directory, setDirectory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [search, setSearch] = useState("");
  const [expandedCompanies, setExpandedCompanies] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [busyAction, setBusyAction] = useState("");
  const [permissions, setPermissions] = useState(EMPTY_PERMISSIONS);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [permissionsError, setPermissionsError] = useState("");
  const mounted = useRef(false);
  const loadVersion = useRef(0);
  const mutationInFlight = useRef(false);
  const permissionRequest = useRef(0);
  const accessLevel = Number(directory?.accessLevel ?? 0);
  const canManage = [2, 3].includes(accessLevel);
  const canDelete = accessLevel === 3;
  const auth = useCallback(
    () => ({
      company_id: companyId,
      code: AuthStorage.getItem("token"),
      loginEmail: AuthStorage.getItem("email"),
    }),
    [companyId],
  );

  const loadDirectory = useCallback(async () => {
    const version = ++loadVersion.current;
    setLoading(true);
    setLoadError("");
    try {
      const response = await CompanyDirectoryService.read(auth());
      if (!mounted.current || version !== loadVersion.current) return;
      setDirectory(response);
      setSelectedRowKeys([]);
      dispatch(saveUserAccessLevel(response.accessLevel));
    } catch (error) {
      if (!mounted.current || version !== loadVersion.current) return;
      setLoadError(
        getErrorMessage(
          error,
          "Unable to load the company directory. Please try again.",
        ),
      );
      setDirectory(null);
      setSelectedRowKeys([]);
    } finally {
      if (mounted.current && version === loadVersion.current) setLoading(false);
    }
  }, [auth, dispatch]);

  useEffect(() => {
    mounted.current = true;
    loadDirectory();
    return () => {
      mounted.current = false;
      loadVersion.current += 1;
      permissionRequest.current += 1;
    };
  }, [loadDirectory]);

  const loadPermissions = async () => {
    const version = ++permissionRequest.current;
    setPermissionsLoading(true);
    setPermissionsError("");
    setPermissions(EMPTY_PERMISSIONS);
    try {
      const response =
        await CompanyDirectoryService.readPermissionTemplates(auth());
      if (!mounted.current || version !== permissionRequest.current) return;
      setPermissions(response);
      if (!response.companyAssignablePermissions.length) {
        setPermissionsError(
          "No company permission templates are available to assign.",
        );
      }
    } catch (error) {
      if (mounted.current && version === permissionRequest.current) {
        setPermissionsError(
          getErrorMessage(
            error,
            "Unable to load permission templates. Please try again.",
          ),
        );
      }
    } finally {
      if (mounted.current && version === permissionRequest.current)
        setPermissionsLoading(false);
    }
  };

  const companyGroups = useMemo(
    () => filterCompanies(directory?.content, search),
    [directory, search],
  );
  const selectedContacts = useMemo(() => {
    const selectedKeys = new Set(selectedRowKeys);
    return filterCompanies(directory?.content).flatMap(
      ({ company, contacts }) =>
        contacts
          .filter(
            (contact) =>
              selectedKeys.has(getContactKey(contact, company)) &&
              canManageContact(accessLevel, contact),
          )
          .map((contact) => ({
            key: getContactKey(contact, company),
            contact,
          })),
    );
  }, [directory, selectedRowKeys, accessLevel]);
  const selectedKeys = selectedContacts.map(({ key }) => key);
  const visibleKeys = new Set(
    companyGroups.flatMap(({ company, contacts }) =>
      contacts.map((contact) => getContactKey(contact, company)),
    ),
  );
  const hiddenSelectedCount = selectedKeys.filter(
    (key) => !visibleKeys.has(key),
  ).length;
  const companyOptions = useMemo(
    () =>
      Object.entries(directory?.content || {}).map(([company, contacts]) => ({
        value: company,
        address: contacts[0]?.["Company Address"] || "",
      })),
    [directory],
  );
  const projectOptions = useMemo(
    () => getProjectOptions(directory?.currentUserProjects),
    [directory],
  );
  const visibleCount = companyGroups.reduce(
    (total, group) => total + group.contacts.length,
    0,
  );
  const companyKeys = companyGroups.map(({ company }) => company);
  const activeKeys = search.trim()
    ? companyKeys
    : (expandedCompanies ?? companyKeys.slice(0, 1));

  const openCreate = () => {
    if (!canManage || loading || busyAction) return;
    setFormError("");
    setDialog({ type: "create" });
    loadPermissions();
  };

  const openEdit = (contact) => {
    if (!canManageContact(accessLevel, contact) || loading || busyAction)
      return;
    setFormError("");
    setDialog({ type: "edit", contact });
    loadPermissions();
  };

  const openBulkEdit = () => {
    if (
      !canManage ||
      loading ||
      busyAction ||
      saving ||
      !selectedContacts.length
    )
      return;
    setFormError("");
    setDialog({
      type: "bulk-edit",
      contacts: selectedContacts.map(({ contact }) => contact),
    });
  };

  const closeDialog = () => {
    if (mutationInFlight.current) return;
    permissionRequest.current += 1;
    setDialog(null);
    setFormError("");
  };

  const submitContact = async (values) => {
    if (!canManage || loading || mutationInFlight.current || !dialog) return;
    if (dialog.contact && !canManageContact(accessLevel, dialog.contact))
      return;
    const isEditing = dialog.type === "edit";
    setFormError("");
    {
      const allowedProjects = new Set(projectOptions.map(({ value }) => value));
      const currentValues = isEditing
        ? getContactFormValues(dialog.contact)
        : null;
      const preservedProjects = (currentValues?.projectIds || []).filter(
        (id) => !allowedProjects.has(id),
      );
      const selectedProjectsToEdit =
        values.projectEditScope === "selected"
          ? values.inviteeProjectsToEdit || []
          : [];
      const finalProjectIds = new Set((values.projectIds || []).map(String));
      if (
        (values.projectIds || []).some(
          (id) => !allowedProjects.has(id) && !preservedProjects.includes(id),
        ) ||
        preservedProjects.some((id) => !values.projectIds?.includes(id))
      ) {
        setFormError("You can only add contacts to projects you belong to.");
        return;
      }
      if (
        isEditing &&
        values.projectEditScope === "selected" &&
        (!selectedProjectsToEdit.length ||
          selectedProjectsToEdit.some(
            (id) =>
              !allowedProjects.has(String(id)) ||
              !finalProjectIds.has(String(id)),
          ))
      ) {
        setFormError("Select at least one assigned project you belong to.");
        return;
      }
      if (
        permissionsLoading ||
        permissionsError ||
        (!permissions.companyAssignablePermissions.includes(
          values.inviteeCompanyPermission,
        ) &&
          values.inviteeCompanyPermission !==
            currentValues?.inviteeCompanyPermission) ||
        (values.projectIds?.length &&
          values.inviteeProjectPermission &&
          !permissions.projectAssignablePermissions.includes(
            values.inviteeProjectPermission,
          ) &&
          values.inviteeProjectPermission !==
            currentValues?.inviteeProjectPermission)
      ) {
        setFormError(
          "Select an available permission template before saving this contact.",
        );
        return;
      }
    }
    mutationInFlight.current = true;
    setSaving(true);
    try {
      if (isEditing) {
        await CompanyDirectoryService.edit({
          ...auth(),
          editDetails: [getContactUpdateDetails(values, dialog.contact)],
        });
      } else {
        const contactDetails = getContactAssignmentDetails(values, {
          "Email Address": values.inviteeEmail.trim(),
        });
        await CompanyDirectoryService.create({ ...auth(), contactDetails });
      }
      if (!mounted.current) return;
      setDialog(null);
      messageApi.success(isEditing ? "Contact updated." : "Contact created.");
      await loadDirectory();
    } catch (error) {
      if (mounted.current)
        setFormError(
          getErrorMessage(
            error,
            isEditing
              ? "Unable to update this contact. Please try again."
              : "Unable to create this contact. Please try again.",
          ),
        );
    } finally {
      mutationInFlight.current = false;
      if (mounted.current) setSaving(false);
    }
  };

  const submitBulkEdit = async (values) => {
    if (loading || mutationInFlight.current || dialog?.type !== "bulk-edit")
      return;
    if (
      !dialog.contacts.length ||
      dialog.contacts.some((contact) => !canManageContact(accessLevel, contact))
    )
      return;
    if (
      !values.inviteeCompany?.trim() &&
      !values.inviteeCompanyAddress?.trim()
    ) {
      setFormError(
        "Enter a company name or address to update the selected contacts.",
      );
      return;
    }
    mutationInFlight.current = true;
    setSaving(true);
    setFormError("");
    try {
      await CompanyDirectoryService.edit({
        ...auth(),
        editDetails: getBulkEditDetails(values, dialog.contacts),
      });
      if (!mounted.current) return;
      setDialog(null);
      setSelectedRowKeys([]);
      messageApi.success(
        `${dialog.contacts.length} ${dialog.contacts.length === 1 ? "contact" : "contacts"} updated.`,
      );
      await loadDirectory();
    } catch (error) {
      if (mounted.current)
        setFormError(
          getErrorMessage(
            error,
            "Unable to update the selected contacts. Please try again.",
          ),
        );
    } finally {
      mutationInFlight.current = false;
      if (mounted.current) setSaving(false);
    }
  };

  const runAction = async (key, action, successMessage) => {
    if (!canManage || loading || mutationInFlight.current) return;
    mutationInFlight.current = true;
    setBusyAction(key);
    try {
      await action();
      if (!mounted.current) return;
      messageApi.success(successMessage);
      await loadDirectory();
    } catch (error) {
      if (mounted.current) {
        messageApi.error(
          getErrorMessage(
            error,
            "The action could not be completed. Please try again.",
          ),
        );
      }
      throw error;
    } finally {
      mutationInFlight.current = false;
      if (mounted.current) setBusyAction("");
    }
  };

  const inviteContact = (contact, company) => {
    if (!canManageContact(accessLevel, contact) || loading || busyAction)
      return;
    const isNew = contact.Status === "Not Invited";
    modalApi.confirm({
      title: isNew ? "Send Invitation" : "Resend Invitation",
      content: `Send an invitation to ${getContactName(contact)} (${contact["Email Address"]})?`,
      okText: "Send Invitation",
      onOk: () =>
        runAction(
          getContactKey(contact, company),
          () =>
            CompanyDirectoryService.invite({
              ...auth(),
              invitationDetails: [getInvitationDetails(contact)],
            }),
          "Invitation sent.",
        ),
    });
  };

  const deleteContact = async (contact, company) => {
    if (
      !canDelete ||
      !canManageContact(accessLevel, contact) ||
      loading ||
      busyAction ||
      saving ||
      dialog ||
      mutationInFlight.current
    )
      return;
    await modalApi.confirm({
      title: "Delete Contact",
      content: `Delete ${getContactName(contact)} (${contact["Email Address"]}) from the company directory?`,
      okText: "Delete Contact",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: () =>
        runAction(
          getContactKey(contact, company),
          () =>
            CompanyDirectoryService.remove({
              ...auth(),
              inviteeEmail: contact["Email Address"],
            }),
          "Contact deleted.",
        ),
    });
  };

  const bulkInvite = async () => {
    if (
      !canManage ||
      loading ||
      busyAction ||
      saving ||
      !selectedContacts.length
    )
      return;
    const contacts = selectedContacts.map(({ contact }) => contact);
    await modalApi.confirm({
      title: "Bulk Invite",
      content: (
        <>
          <p className={styles.paragraph}>
            Send invitations to {contacts.length} selected{" "}
            {contacts.length === 1 ? "contact" : "contacts"}?
          </p>
          <List
            size="small"
            style={{ maxHeight: 240, overflowY: "auto" }}
            dataSource={contacts}
            renderItem={(contact) => (
              <List.Item>
                <div className={styles.contactStack}>
                  <span>{getContactName(contact)}</span>
                  <span className={styles.secondary}>
                    {contact["Email Address"]}
                  </span>
                </div>
              </List.Item>
            )}
          />
        </>
      ),
      okText: "Send Invitations",
      onOk: () =>
        runAction(
          "bulk-invite",
          () =>
            CompanyDirectoryService.invite({
              ...auth(),
              invitationDetails: contacts.map(getInvitationDetails),
            }),
          "Invitations sent.",
        ),
    });
  };

  const rowSelection = (company, contacts) => {
    const groupKeys = new Set(
      contacts.map((contact) => getContactKey(contact, company)),
    );
    const eligibleKeys = new Set(
      contacts
        .filter((contact) => canManageContact(accessLevel, contact))
        .map((contact) => getContactKey(contact, company)),
    );
    return {
      selectedRowKeys: selectedKeys.filter((key) => groupKeys.has(key)),
      preserveSelectedRowKeys: true,
      columnWidth: 48,
      onChange: (keys) => {
        if (!canManage || loading || busyAction || saving || dialog) return;
        setSelectedRowKeys((previous) => [
          ...previous.filter((key) => !groupKeys.has(key)),
          ...keys.filter((key) => eligibleKeys.has(key)),
        ]);
      },
      getCheckboxProps: (contact) => ({
        disabled:
          !canManageContact(accessLevel, contact) ||
          loading ||
          Boolean(busyAction) ||
          saving ||
          Boolean(dialog),
        "aria-label": `Select ${getContactName(contact)}`,
      }),
    };
  };

  const syncContacts = () => {
    if (!canManage || loading || busyAction) return;
    modalApi.confirm({
      title: "Sync Company Contacts with Procore",
      content: "Refresh this company directory with contacts from Procore?",
      okText: "Sync Contacts",
      onOk: () =>
        runAction(
          "sync",
          () => CompanyDirectoryService.sync(auth()),
          "Company contacts synced.",
        ),
    });
  };

  const syncButton = (primary = false) => (
    <Button
      className={styles.syncButton}
      type={primary ? "primary" : "default"}
      icon={
        <img
          src={primary ? ProcoreLogoWhite : ProcoreLogo}
          alt=""
          width={16}
          height={16}
        />
      }
      onClick={syncContacts}
      disabled={!canManage || loading || Boolean(busyAction)}
      loading={busyAction === "sync"}
    >
      Sync with Procore
    </Button>
  );

  const columns = (company) => [
    {
      title: "Contact",
      key: "contact",
      width: 250,
      sorter: (a, b) => getContactName(a).localeCompare(getContactName(b)),
      render: (_, contact) => (
        <>
          <div className={styles.contactName}>{getContactName(contact)}</div>
          <span className={`${styles.secondary} ${styles.email}`}>
            {contact["Email Address"] || "—"}
          </span>
        </>
      ),
    },
    {
      title: "Phone",
      dataIndex: "Phone",
      width: 160,
      render: (value) => value || "—",
    },
    {
      title: "Company Address",
      dataIndex: "Company Address",
      width: 220,
      render: (value) => value || "—",
    },
    {
      title: "Projects",
      key: "projects",
      width: 140,
      render: (_, contact) => {
        const projects = getContactProjects(contact);
        if (!projects.length)
          return <span className={styles.secondary}>None</span>;
        return (
          <Popover
            trigger="click"
            placement="bottomLeft"
            title={`${getContactName(contact)}’s projects`}
            content={
              <List
                className={styles.projectList}
                size="small"
                dataSource={projects}
                renderItem={(project) => (
                  <List.Item key={project.value}>{project.label}</List.Item>
                )}
              />
            }
          >
            <Button
              type="link"
              size="small"
              className={styles.projectCount}
              aria-label={`View ${projects.length} projects for ${getContactName(contact)}`}
            >
              {projects.length} {projects.length === 1 ? "project" : "projects"}
              <DownOutlined />
            </Button>
          </Popover>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "Status",
      width: 130,
      render: (status) => (
        <Tag color={STATUS_COLORS[status] || "default"}>
          {status || "Unknown"}
        </Tag>
      ),
    },
    ...(canManage
      ? [
          {
            title: "Actions",
            key: "actions",
            width: 85,
            fixed: "right",
            render: (_, contact) =>
              canManageContact(accessLevel, contact) ? (
                <Dropdown
                  trigger={["click"]}
                  disabled={Boolean(busyAction) || loading || saving}
                  menu={{
                    items: [
                      {
                        key: "edit",
                        icon: <EditOutlined />,
                        label: "Edit Contact Info",
                      },
                      {
                        key: "invite",
                        icon: <SendOutlined />,
                        label:
                          contact.Status === "Not Invited"
                            ? "Send Invitation"
                            : "Resend Invitation",
                      },
                      ...(canDelete
                        ? [
                            {
                              key: "delete",
                              icon: <DeleteOutlined />,
                              label: "Delete Contact",
                              danger: true,
                            },
                          ]
                        : []),
                    ],
                    onClick: ({ key }) => {
                      if (key === "edit") openEdit(contact);
                      else if (key === "invite")
                        inviteContact(contact, company);
                      else if (key === "delete")
                        deleteContact(contact, company);
                    },
                  }}
                >
                  <Button
                    type="text"
                    icon={<MoreOutlined />}
                    loading={busyAction === getContactKey(contact, company)}
                    aria-label={`Actions for ${getContactName(contact)}`}
                  />
                </Dropdown>
              ) : (
                <span className={styles.secondary}>—</span>
              ),
          },
        ]
      : []),
  ];

  return (
    <>
      {messageContext}
      {modalContext}
      <Permissions pageType="company_directory" />
      <main className={styles.page}>
        {loadError ? (
          <Alert
            type="error"
            showIcon
            message={loadError}
            action={<Button onClick={loadDirectory}>Retry</Button>}
          />
        ) : loading && !directory ? (
          <div style={{ padding: 64, textAlign: "center" }}>
            <Spin size="large" indicator={<LoadingComponent />} />
            {/* <p>Loading company directory…</p> */}
          </div>
        ) : accessLevel === 0 ? (
          <div className={`d-flex flex-column ${styles.pageWrapper}`}>
            <div className="row w-100 d-flex justify-content-center p-5">
              <div className="d-flex flex-column align-items-center text-center gap-3">
                {/* <LockRoundedIcon style={{ fontSize: "5rem", color: "#999" }} /> */}
                <Text>
                  You do not have the necessary permissions to access this page.
                </Text>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className={styles.header}>
              <div>
                {/* <h3 className={styles.title}>Company Directory</h3> */}
              </div>
              <div className={styles.inlineGroup}>
                {companyGroups.length > 0 && syncButton()}
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={openCreate}
                  disabled={!canManage || loading || Boolean(busyAction)}
                >
                  Create Contact
                </Button>
              </div>
            </div>
            <div className={styles.toolbar}>
              <Input
                className={styles.search}
                prefix={<SearchOutlined />}
                placeholder="Search contacts, companies or projects"
                aria-label="Search company directory"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                allowClear
              />
              <div className={styles.inlineGroup}>
                {selectedContacts.length > 0 ? (
                  <>
                    <strong>{selectedContacts.length} selected</strong>
                    {hiddenSelectedCount > 0 && (
                      <span className={styles.secondary}>
                        ({hiddenSelectedCount} hidden by search)
                      </span>
                    )}
                    <Dropdown
                      trigger={["click"]}
                      disabled={loading || Boolean(busyAction) || saving}
                      menu={{
                        items: [
                          {
                            key: "edit",
                            label: "Bulk Edit",
                            icon: <EditOutlined />,
                          },
                          {
                            key: "invite",
                            label: "Bulk Invite",
                            icon: <SendOutlined />,
                          },
                        ],
                        onClick: ({ key }) =>
                          key === "edit" ? openBulkEdit() : bulkInvite(),
                      }}
                    >
                      <Button
                        loading={busyAction === "bulk-invite"}
                        disabled={loading || Boolean(busyAction) || saving}
                      >
                        Bulk Actions <DownOutlined />
                      </Button>
                    </Dropdown>
                    <Button
                      type="link"
                      onClick={() => setSelectedRowKeys([])}
                      disabled={loading || Boolean(busyAction) || saving}
                    >
                      Clear Selection
                    </Button>
                  </>
                ) : (
                  <span className={styles.secondary}>
                    {visibleCount} {visibleCount === 1 ? "contact" : "contacts"}{" "}
                    in {companyGroups.length}{" "}
                    {companyGroups.length === 1 ? "company" : "companies"}
                  </span>
                )}
                {companyGroups.length > 0 && !search.trim() && (
                  <Button
                    type="link"
                    onClick={() =>
                      setExpandedCompanies(
                        activeKeys.length === companyKeys.length
                          ? []
                          : companyKeys,
                      )
                    }
                  >
                    {activeKeys.length === companyKeys.length
                      ? "Collapse all"
                      : "Expand all"}
                  </Button>
                )}
              </div>
            </div>
            <Spin spinning={loading} indicator={<LoadingComponent />}>
              {companyGroups.length ? (
                <Collapse
                  className={styles.companies}
                  activeKey={activeKeys}
                  onChange={setExpandedCompanies}
                  items={companyGroups.map(({ company, contacts }) => ({
                    key: company,
                    label: (
                      <div className={styles.companyLabel}>
                        <strong>{company || "No company"}</strong>
                        <Badge
                          count={contacts.length}
                          showZero
                          className={styles.companyCount}
                        />
                      </div>
                    ),
                    children: (
                      <Table
                        rowKey={(contact) => getContactKey(contact, company)}
                        columns={columns(company)}
                        dataSource={contacts}
                        rowSelection={
                          canManage
                            ? rowSelection(company, contacts)
                            : undefined
                        }
                        size="middle"
                        scroll={{ x: canManage ? 1033 : 985 }}
                        pagination={{
                          defaultPageSize: 10,
                          showSizeChanger: true,
                          hideOnSinglePage: true,
                        }}
                      />
                    ),
                  }))}
                />
              ) : (
                <Empty
                  className={styles.emptyState}
                  description={
                    <div className={styles.emptyDescription}>
                      <strong>
                        {search.trim()
                          ? "No contacts match your search"
                          : "No company contacts yet"}
                      </strong>
                      <span className={styles.secondary}>
                        {search.trim()
                          ? "Try a different search or sync the latest contacts from Procore."
                          : "Sync with Procore to bring your company contacts into this directory."}
                      </span>
                    </div>
                  }
                >
                  <div className={styles.inlineGroup}>
                    {search.trim() && (
                      <Button onClick={() => setSearch("")}>
                        Clear Search
                      </Button>
                    )}
                    {syncButton(true)}
                  </div>
                </Empty>
              )}
            </Spin>
          </>
        )}
      </main>
      {dialog?.type === "bulk-edit" && canManage && (
        <BulkEditForm
          count={dialog.contacts.length}
          companies={companyOptions}
          saving={saving}
          error={formError}
          onCancel={closeDialog}
          onSubmit={submitBulkEdit}
        />
      )}
      {dialog && dialog.type !== "bulk-edit" && canManage && (
        <ContactForm
          editingContact={dialog.contact}
          saving={saving}
          error={formError}
          companies={companyOptions}
          projectOptions={projectOptions}
          permissions={permissions}
          permissionsLoading={permissionsLoading}
          permissionsError={permissionsError}
          onRetryPermissions={loadPermissions}
          onCancel={closeDialog}
          onSubmit={submitContact}
        />
      )}
    </>
  );
};

const CompanyDirectory = () => {
  const { company_id } = useParams();
  return <DirectoryContent key={company_id} companyId={company_id} />;
};

export default CompanyDirectory;
